import React, { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { toast } from "react-toastify";
import { getAttendanceSummary, updateAttendanceSummary, uploadHolidays } from "../../http";
import Papa from "papaparse";
import * as XLSX from "xlsx";

const AttendanceSummary = () => {
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false); 
  const [showModal, setShowModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formData, setFormData] = useState({
    present: "",
    halfDays: "",
    allowedLeaves: ""
  });
  const [uploading, setUploading] = useState(false);

  const years = [2020, 2021, 2022, 2023, 2024, 2025];
  const months = [
    { name: "January", value: 1 },
    { name: "February", value: 2 },
    { name: "March", value: 3 },
    { name: "April", value: 4 },
    { name: "May", value: 5 },
    { name: "June", value: 6 },
    { name: "July", value: 7 },
    { name: "August", value: 8 },
    { name: "September", value: 9 },
    { name: "October", value: 10 },
    { name: "November", value: 11 },
    { name: "December", value: 12 },
  ];

  useEffect(() => {
    const dt = new Date();
    const currentYear = dt.getFullYear();
    const currentMonth = dt.getMonth() + 1; 

    setSelectedYear(currentYear);
    setSelectedMonth(currentMonth);
    
    fetchAttendanceSummary(currentMonth, currentYear);
  }, []);

  const fetchAttendanceSummary = async (month, year) => {
    if (!month || !year) {
      toast.error("Please select both month and year");
      return;
    }

    setLoading(true);
    try {
      const response = await getAttendanceSummary({
        month: parseInt(month),
        year: parseInt(year)
      });

      if (response.error) {
        toast.error(response.error);
        setSummaryData([]);
        setLoading(false);
        return;
      }

      // Handle different response structures
      let dataArray = [];
      if (Array.isArray(response)) {
        // Direct array response
        dataArray = response;
      } else if (response && typeof response === 'object' && response.status === 200) {
        // Object with numeric indices (like {0: {...}, 1: {...}, status: 200})
        dataArray = Object.keys(response)
          .filter(key => !isNaN(key) && response[key] && response[key]._id)
          .map(key => response[key]);
      } else {
        setSummaryData([]);
        setLoading(false);
        return;
      }

      const transformedData = dataArray.map((item, index) => ({
        id: item._id,
        employeeId: item.employeeID?._id,
        name: item.employeeID?.name,
        email: item.employeeID?.email,
        present: item.present,
        halfday: item.halfDays,
        absent: item.absent,
        allowedLeaves: item.allowedLeaves,
        sundays: item.sundays,      
        paidDays: item.paidDays,
        month: item.month,
        year: item.year
      }));

      setSummaryData(transformedData);
      
      if (transformedData.length === 0) {
        toast.info("No attendance data found for the selected period");
      }
    } catch (error) {
      console.error("Error fetching attendance summary:", error);
      toast.error("Failed to fetch attendance data");
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  };

  const searchSummary = () => {
    if (!selectedMonth || !selectedYear) {
      toast.error("Please select both month and year");
      return;
    }
    fetchAttendanceSummary(selectedMonth, selectedYear);
  };

  const handleEditClick = (record) => {
    setSelectedRecord(record);
    setFormData({
      present: record.present,
      halfDays: record.halfday,
      allowedLeaves: record.allowedLeaves
    });
    setShowModal(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const { present, halfDays, allowedLeaves } = formData;
    
    if (present === "" || halfDays === "" || allowedLeaves === "") {
      toast.error("All fields are required");
      return false;
    }
    
    if (present < 0 || halfDays < 0 || allowedLeaves < 0) {
      toast.error("Values cannot be negative");
      return false;
    }
    
    if (isNaN(present) || isNaN(halfDays) || isNaN(allowedLeaves)) {
      toast.error("Please enter valid numbers");
      return false;
    }
    
    return true;
  };

  const handleSaveChanges = async () => {
    if (!validateForm()) {
      return;
    }

    setUpdateLoading(true);
    
    try {
      const response = await updateAttendanceSummary({
        id: selectedRecord.id, 
        present: parseInt(formData.present),
        halfDays: parseInt(formData.halfDays),
        allowedLeaves: parseInt(formData.allowedLeaves)
      });

      if (response.success) {
       
        const updatedData = summaryData.map((record) => {
          if (record.id === selectedRecord.id) {
            return {
              ...record,
              present: response.data.present,
              halfday: response.data.halfDays,
              allowedLeaves: response.data.allowedLeaves,
              paidDays: response.data.paidDays,
            };
          }
          return record;
        });

        setSummaryData(updatedData);
        setShowModal(false);
        setSelectedRecord(null);
        setFormData({
          present: "",
          halfDays: "",
          allowedLeaves: ""
        });
        toast.success(response.message || "Attendance summary updated successfully!");
      } else {
        toast.error(response.error || "Failed to update attendance summary");
      }
      
    } catch (error) {
      console.error("Error updating attendance summary:", error);
      toast.error(error.message || "Failed to update attendance summary");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRecord(null);
    setFormData({
      present: "",
      halfDays: "",
      allowedLeaves: ""
    });
  };

  const getMonthName = (monthValue) => {
    const month = months.find(m => m.value === parseInt(monthValue));
    return month ? month.name : '';
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const isCSV = file.name.endsWith('.csv');
    const isXLSX = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    if (isCSV) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          await handleHolidayUploadResults(results.data);
        },
        error: () => {
          toast.error("Failed to parse CSV");
          setUploading(false);
        }
      });
    } else if (isXLSX) {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
          await handleHolidayUploadResults(json);
        } catch (err) {
          toast.error("Failed to parse Excel file");
          setUploading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      toast.error("Unsupported file type. Please upload a CSV or Excel file.");
      setUploading(false);
    }
  };

  const handleHolidayUploadResults = async (rows) => {
    try {
      if (!selectedYear) {
        toast.error("Please select a year for the holidays");
        setUploading(false);
        return;
      }
      // Expecting columns: slno,holidayname,date,day
      const holidays = rows.map(row => ({
        holiday: row.holidayname,
        date: new Date(row.date),
        day: row.day
      })).filter(h => h.holiday && h.date && h.day);
      if (!holidays.length) {
        toast.error("No valid holidays found in file");
        setUploading(false);
        return;
      }
      const res = await uploadHolidays({ year: parseInt(selectedYear), holidays });
      if (res.success) {
        toast.success("Holidays uploaded successfully!");
      } else {
        toast.error(res.message || "Failed to upload holidays");
      }
    } catch (err) {
      toast.error("Error uploading holidays");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="main-content">
      <section className="section">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap" style={{gap: 0}}>
            <h4 className="mb-0">Attendance Summary</h4>
            <div className="d-flex flex-row align-items-center" style={{gap: '8px', flexWrap: 'nowrap'}}>
              <CSVLink
                data={[
                  [
                    "index",
                    "Name",
                    "Email",
                    "Present",
                    "Halfday",
                    "Absent",
                    "Allowed Leaves",
                    "Sundays",        
                    "Paid Days",
                  ],
                  ...summaryData?.map((record, index) => [
                    index + 1,
                    record.name,
                    record.email,
                    record.present,
                    record.halfday,
                    record.absent,
                    record.allowedLeaves,
                    record.sundays,   
                    record.paidDays,
                  ]),
                ]}
                filename={`attendance_summary_${getMonthName(selectedMonth)}_${selectedYear}_$${new Date().toISOString().split("T")[0]}.csv`}
                className="btn btn-success"
                target="_blank"
              >
                Download CSV
              </CSVLink>
              <label className="btn btn-primary mb-0" style={{whiteSpace: 'nowrap'}}>
                {uploading ? "Uploading..." : "Upload Holidays"}
                <input
                  type="file"
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  onChange={handleCSVUpload}
                  style={{ display: "none" }}
                  disabled={uploading}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap justify-content-center w-100">
          <div className="col-md col-12 mb-2">
            <select
              className="form-control select2 w-100"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              disabled={uploading}
            >
              <option value="">Select Year</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md col-12 mb-2">
            <select
              className="form-control select2 w-100"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="">Select Month</option>
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.name}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={searchSummary}
            className="btn btn-lg btn-primary col-md col-12 mb-2"
            disabled={loading}
          >
            {loading ? "Loading..." : "Search"}
          </button>
        </div>
      </section>

      <div className="table-responsive">
        <table className="table table-striped table-md center-text">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Present</th>
              <th>Halfday</th>
              <th>Absent</th>
              <th>Allowed Leaves</th>
              <th>Sundays</th>  
              <th>Paid Days</th>
              <th>Edit</th>
            </tr>
          </thead>
         <tbody>
            {summaryData?.map((record, idx) => (
              <tr key={record.id}>
                <td>{idx + 1}</td>
                <td>{record.name}</td>
                <td>{record.email}</td>
                <td className="text-success">{record.present}</td>
                <td className="text-warning">{record.halfday}</td>
                <td className="text-danger">{record.absent}</td>
                <td>{record.allowedLeaves}</td>
                <td>{record.sundays}</td>  
                <td>{record.paidDays}</td>
                <td>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleEditClick(record)}
                    disabled={updateLoading}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Attendance Summary</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  disabled={updateLoading}
                ></button>
              </div>
              <div className="modal-body">
                
                
                <div className="mb-3">
                  <label className="form-label">Present Days</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.present}
                    onChange={(e) => handleInputChange('present', e.target.value)}
                    min="0"
                    disabled={updateLoading}
                    placeholder="Enter present days"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Half Days</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.halfDays}
                    onChange={(e) => handleInputChange('halfDays', e.target.value)}
                    min="0"
                    disabled={updateLoading}
                    placeholder="Enter half days"
                  />
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Allowed Leaves</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.allowedLeaves}
                    onChange={(e) => handleInputChange('allowedLeaves', e.target.value)}
                    min="0"
                    disabled={updateLoading}
                    placeholder="Enter allowed leaves"
                  />
                </div>

                <div className="alert alert-info">
                  <small>
                    <strong>Note:</strong> Paid Days will be automatically calculated as: Present Days + (Half Days × 0.5) + Allowed Leaves + Sundays
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                  disabled={updateLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveChanges}
                  disabled={updateLoading || !formData.present || !formData.halfDays || !formData.allowedLeaves}
                >
                  {updateLoading ? "Updating..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceSummary;